import Select, {
	components,
	DropdownIndicatorProps,
	MultiValueRemoveProps,
} from "react-select";

import React, { useEffect, useState, useRef } from "react";

import Link from "next/link";

import { Button } from "@/components/ui/button";

import { Textarea } from "@/components/ui/textarea";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { cn } from "@/lib/utils";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "@/components/ui/command";

import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

import { ScrollArea } from "@/components/ui/scroll-area";

import { Input } from "@/components/ui/input";

import { useToast } from "@/components/ui/use-toast";

import {
	Edit,
	X,
	ChevronsUpDown,
	ChevronDown,
	ChevronUp,
	Check,
	CheckCircle,
} from "lucide-react";

import { countriesList } from "@/models/countries-list";
import { languages } from "@/models/languages";
import { MixpanelTracking } from "@/lib/mixpanel-browser-analytics";

type Address = {
	__typename?: "Address";
	city?: string;
	country?: string;
	state?: string;
};

type Business = {
	address?: Address;
	name?: string;
	description?: string;
	email?: string;
	payments?: Array<string | null>;
	phone?: string;
	website?: string;
	languages?: Array<string | null>;
};

type Props = {
	userId: string;
	isMember: boolean;
	isAdmin: boolean;
};

export function BusinessDetails({ userId, isMember, isAdmin }: Props) {
	console.log("------------------RERENDER BUSINESS AND OTHER PAGES");
	const { toast } = useToast();

	const [business, setBusiness] = useState<Business>();
	const [sampleDescription, setSampleDescription] = useState<any>();
	const [readMore, setReadMore] = useState<boolean>(false);

	const [editable, setEditable] = useState(isMember || isAdmin);
	const [showForm, setShowForm] = useState(false);

	const [tooManyPaymentMethods, setTooManyPaymentMethods] = useState(false);
	const [payments, setPayments] = useState<any[]>([]);
	const [spokenLangs, setSpokenLangs] = useState<any[]>([]);

	let languageList: any[] = [];

	languages.forEach(function (element) {
		languageList.push({ label: element, value: element });
	});

	useEffect(() => {
		fetch(`/api/user/profile?id=${userId}&area=business`)
			.then((res) => res.json())
			.then((response) => {
				console.log("Updating Business on Mount ", response?.business);
				setBusiness(response?.business);
				setPayments(response?.business?.payments || []);
				setSpokenLangs(response?.business?.languages || []);

				setSampleDescription(
					response?.business?.description?.slice(0, 350)
				);
				setReadMore(
					response?.business?.description?.length > 350 ? true : false
				);

				const checkIfUserIsAdmin = () => {
					if (isMember || isAdmin) {
						setEditable(true);
					} else {
						setEditable(false);
					}
				};

				// checkIfUserIsAdmin();
			});
	}, []);

	const countries = countriesList.map((country) => {
		return { label: country, value: country.toLowerCase() };
	});

	const profileFormSchema = z.object({
		languages: z.string().array(),
		payments: z.string().array(),
		company: z
			.string()
			.max(60, {
				message: "Company name must not be longer than 60 characters.",
			})
			.optional()
			.default(""),

		description: z
			.string()
			.max(1400, {
				message: "Description is too long.",
			})
			.optional()
			.default(""),

		email: z.union([
			z.literal(""),
			z.string().email().optional().default(""),
		]),

		website: z
			.union([
				z.literal(""),
				z.string().trim().url({
					message:
						"Please enter a valid URL. It should start with https://",
				}),
			])
			.optional()
			.default(""),

		phone: z.string().optional().default(""),

		city: z.string().optional().default(""),

		state: z.string().optional().default(""),

		country: z.string().optional().default(""),

		new: z.string().optional(),
	});

	type ProfileFormValues = z.infer<typeof profileFormSchema>;

	const defaultValues: Partial<ProfileFormValues> = {
		company: business?.name,
		description: business?.description,
		email: business?.email,
		website: business?.website,
		phone: business?.phone,
		city: business?.address?.city,
		state: business?.address?.state,
		country: business?.address?.country?.trim().toLowerCase(),
		payments: payments,
		languages: spokenLangs,
		new: "",
	};

	const ProfileForm = () => {
		const form = useForm<ProfileFormValues>({
			resolver: zodResolver(profileFormSchema),
			defaultValues,
			mode: "onChange",
		});

		const onKeyDown = (e: any) => {
			if (e.key !== "Enter") return;
			const value = e.target.value;
			if (!value.trim()) return;

			if (payments.length === 5) {
				setPayments((prev) => {
					return [...prev, value];
				});
				setTooManyPaymentMethods(true);
			} else if (payments.length <= 4) {
				setPayments([...payments, value]);
			}

			e.target.value = "";
		};

		const removeTag = (index: number) => {
			setPayments(payments.filter((el: any, i: any) => i !== index));
			setTooManyPaymentMethods(false);
		};

		const handleLangChange = (e: any) => {
			setSpokenLangs(Array.isArray(e) ? e.map((x) => x.value) : []);
		};

		const onSubmit = async (data: ProfileFormValues) => {
			data.payments = [...payments];
			let business: Business = {
				name: data.company,
				description: data.description,
				email: data.email,
				payments: data.payments,
				phone: data.phone,
				website: data.website,
				languages: data.languages,
				address: {
					city: data.city,
					country: data.country,
					state: data.state,
				},
			};

			setSampleDescription(data.description?.slice(0, 350));
			setReadMore(data.description?.length > 350 ? true : false);

			setShowForm(false);

			const toastId = toast({
				title: "Saving...",
			});

			try {
				const res = await fetch("/api/user/business", {
					method: "PUT",
					body: JSON.stringify({ userId, data: business }),
				});
				const result = await res.json();
				console.log("RESULT FROM UPDATE ", result);
				toastId.update(
					toast({
						variant: "default",
						duration: 1300,
						action: (
							<div className="w-full flex items-center !ml-0">
								<CheckCircle color="#22c55e" className="mr-2" />
								<span className="text-sm font-semibold">Saved</span>
							</div>
						),
					})
				);
				setBusiness(business);
			} catch (e: any) {
				console.log("The error is", e);
				toastId.update(
					toast({
						title: "Something went wrong",
						duration: 1300,
						description: e.message,
						variant: "destructive",
					})
				);
			}
		};

		const DropdownIndicator = (props: DropdownIndicatorProps) => {
			return (
				<components.DropdownIndicator {...props}>
					<ChevronDown />
				</components.DropdownIndicator>
			);
		};

		const MultiValueRemove = (props: MultiValueRemoveProps) => {
			return (
				<components.MultiValueRemove {...props}>
					<X />
				</components.MultiValueRemove>
			);
		};

		const colourStyles = {
			control: ({ isFocused }: any) =>
				`p-1 border-[1px] border-slate-200 rounded-lg ${
					isFocused ? "ring-2 ring-offset-2 ring-slate-400" : ""
				} `,

			placeholder: () => "text-gray-500 pl-2 py-0.5",

			multiValueRemove: () => "hover:pointer text-black",

			input: () => "pl-1 py-0.5",

			valueContainer: () => "p-1 gap-1",

			singleValue: () => "leading-7 ml-1",

			multiValue: () =>
				"bg-gray-200 rounded-lg items-center py-0.5 pl-2 pr-1 gap-1.5",

			dropdownIndicator: () =>
				"p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black",

			menu: () => "p-5 mt-3 border border-gray-200 bg-white rounded-lg",

			option: ({ isFocused }: any) =>
				`hover:cursor-pointer w-1/2 px-3 py-2 rounded ${
					isFocused ? "bg-gray-100 active:bg-gray-200" : ""
				}`,

			noOptionsMessage: () =>
				"text-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm",
		};

		const scrollBarStyles = {
			menuList: (base: any) => ({
				...base,

				scrollbarColor: "#d3d2d2 white",
				scrollbarWidth: "thin",

				"::-webkit-scrollbar": {
					width: "4px",
					height: "8px",
				},

				"::-webkit-scrollbar-thumb": {
					background: "#d3d2d2",
					borderRadius: "100px",
				},
			}),
		};

		return (
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
					{payments.map((tag, index: any) => (
						<div
							className="bg-green-300 p-2 rounded-full mr-2 inline-flex "
							key={tag}>
							<span>{tag}</span>&nbsp;
							<span onClick={() => removeTag(index)}>
								<X className="hover:cursor-pointer" />
							</span>
						</div>
					))}

					{!tooManyPaymentMethods && (
						<FormField
							control={form.control}
							name="new"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Add Payment Method</FormLabel>
									<FormDescription>
										You can add up to 6 payment methods
									</FormDescription>
									<FormControl>
										<Input
											placeholder="Type and press enter to add payment method"
											{...field}
											onKeyDown={onKeyDown}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<FormField
						control={form.control}
						name="languages"
						render={({ field }) => (
							<FormItem>
								<FormLabel htmlFor="langSelect">
									Languages Spoken
								</FormLabel>
								<FormControl id="langSelect">
									<Select
										{...field}
										classNames={colourStyles}
										closeMenuOnSelect={false}
										id="langSelect"
										components={{
											DropdownIndicator,
											MultiValueRemove,
										}}
										value={languageList.filter((obj) =>
											spokenLangs.includes(obj.value)
										)}
										onChange={handleLangChange}
										isMulti
										isClearable={false}
										options={languageList}
										placeholder={"Select Language..."}
										unstyled
										styles={scrollBarStyles}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="company"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Company Name</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Description</FormLabel>
								<FormControl>
									<Textarea
										placeholder="Tell us a little bit about yourself"
										{...field}
									/>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="website"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Website</FormLabel>
								<FormControl>
									<Input
										placeholder="https://www.google.com/"
										{...field}
									/>
								</FormControl>

								<FormDescription>
									Eg. https://www.google.com/
								</FormDescription>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Phone Number</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="city"
						render={({ field }) => (
							<FormItem>
								<FormLabel>City</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="state"
						render={({ field }) => (
							<FormItem>
								<FormLabel>State / Province</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="country"
						render={({ field }) => (
							<FormItem className="">
								<Popover>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												variant="outline"
												role="combobox"
												className={cn(
													"w-full justify-between sm:w-[200px]",
													!field.value && "text-muted-foreground"
												)}>
												{field.value
													? countries.find(
															(country) =>
																country.value === field.value
													  )?.label
													: "Select Country"}
												<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent className="w-[200px] p-0">
										<Command>
											<CommandInput placeholder="Search Countries..." />
											<CommandEmpty>
												Couldn&apos;t find country.
											</CommandEmpty>
											<CommandGroup>
												<ScrollArea className="h-[200px] p-2">
													{countries.map((country) => (
														<CommandItem
															value={country.value}
															key={country.value}
															onSelect={(value) => {
																form.setValue("country", value);
															}}>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	country.value === field.value
																		? "opacity-100"
																		: "opacity-0"
																)}
															/>
															{country.label}
														</CommandItem>
													))}
												</ScrollArea>
											</CommandGroup>
										</Command>
									</PopoverContent>
								</Popover>
								<FormDescription />
								<FormMessage />
							</FormItem>
						)}
					/>

					<Button type="submit" className="hover:bg-blue-500">
						Update profile
					</Button>
				</form>
			</Form>
		);
	};

	return (
		<div className=" flex flex-col flex-wrap items-start sm:items-center">
			{editable && (
				<>
					{showForm && (
						<div className="transition-all fixed top-0 left-0 m-0 p-0 w-[100%] h-[100%] bg-white/80 backdrop-blur-[3px]"></div>
					)}
					<Dialog open={showForm} onOpenChange={setShowForm} modal={false}>
						<DialogTrigger asChild>
							<Button className="hover:bg-blue-500 self-start">
								<Edit color="#ffffff" className="mr-2" />
								Edit Profile
							</Button>
						</DialogTrigger>

						<DialogContent className="dialogScrollbar h-[625px] overflow-y-auto sm:max-w-[425px] ">
							<DialogHeader>
								<DialogTitle className="pt-3">Edit Profile</DialogTitle>
								<DialogDescription>
									Edit profile details
								</DialogDescription>
							</DialogHeader>

							<ProfileForm />
						</DialogContent>
					</Dialog>
				</>
			)}

			<p
				id="title"
				className={`mt-7 mb-4 font-extrabold break-words text-5xl ${
					(!business?.name || business?.name === "") && "text-zinc-200"
				}`}>
				{!business?.name || business?.name === ""
					? "No company name set"
					: business?.name}
			</p>

			{business &&
				business.description &&
				business?.description?.length > 0 && (
					<div className="flex flex-col max-w-[650px]">
						{readMore ? (
							<>
								<p className="text-xl whitespace-pre-line">{`${sampleDescription}...`}</p>

								<Button
									className="pl-0 text-left justify-start max-w-[150px] mt-3 font-bold text-black bg-transparent rounded-none transition-colors ease-in-out duration-100 hover:bg-transparent hover:text-blue-300"
									onClick={() => setReadMore((prev) => !prev)}>
									Read More
									<ChevronDown />
								</Button>
							</>
						) : (
							<>
								<p className="text-xl whitespace-pre-line">
									{business?.description}
								</p>

								{business?.description.length > 350 && (
									<a href="#title">
										<Button
											className="pl-0 text-left justify-start max-w-[150px] mt-3 font-bold text-black bg-transparent rounded-none transition-colors ease-in-out duration-100 hover:bg-transparent hover:text-blue-300"
											onClick={() => setReadMore((prev) => !prev)}>
											Hide
											<ChevronUp />
										</Button>
									</a>
								)}
							</>
						)}
					</div>
				)}

			<p className="text-zinc-500 mt-3">{`${business?.address?.city ?? ""} ${
				business?.address?.state ?? ""
			} ${business?.address?.country ?? ""}`}</p>

			<h5 className="text-lg font-semibold mt-10">Contact</h5>

			<div className="flex flex-col gap-5 flex-wrap sm:flex-row">
				{business?.email && (
					<span>
						<h5 className="text-md font-medium">Email</h5>
						<span className="text-md">|&nbsp;{business?.email}</span>
					</span>
				)}
				{business?.phone && (
					<span>
						<h5 className="text-md font-medium ml-0 sm:ml-2">Phone</h5>
						<span className="text-md ml-0 sm:ml-2">
							|&nbsp;{business?.phone}
						</span>
					</span>
				)}

				{business?.website && (
					<span>
						<h5 className="text-md font-medium ml-0 sm:ml-2">Website</h5>

						<span className="text-md ml-0 sm:ml-2">
							|&nbsp;
							<Link
								href={business?.website}
								onClick={() =>
									MixpanelTracking.getInstance().businessWebsiteLinkClicked(
										{
											user_id: userId,
											business_website: business?.website ?? "",
										}
									)
								}
								className="break-all text-blue-500 font-medium transition-all duration-400 origin-left border-b-2 border-transparent hover:border-blue-500 ">
								{business?.website}
							</Link>
						</span>
					</span>
				)}
			</div>

			<h5 className="text-lg font-semibold mt-10 mb-3">Accepted Payments</h5>

			<div className="flex flex-wrap space-y-2 items-end">
				{business?.payments?.length === 0 || business?.payments === null ? (
					<p className="text-zinc-500">No payment method set</p>
				) : (
					business?.payments?.map((paymentMethod: any, i: number) => {
						return (
							<span
								key={i}
								className="bg-green-300 p-2 rounded-full mr-2 ">
								{paymentMethod}
							</span>
						);
					})
				)}
			</div>

			<h5 className="text-lg font-semibold mt-10 mb-3">Languages Spoken</h5>

			<div className="flex flex-wrap space-y-2 items-end">
				{business?.languages?.length === 0 ||
				business?.languages === null ? (
					<p className="text-zinc-500">No language set</p>
				) : (
					business?.languages?.map((language: any, i: number) => {
						return (
							<span key={i} className="bg-zinc-100 p-2 rounded-lg mr-2 ">
								{language}
							</span>
						);
					})
				)}
			</div>
		</div>
	);
}
